import { Skill, SkillValues, skillParams } from '@/lib/skills';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  FileSpec,
  IStep,
  commandsState,
  sessionState,
  useAuth,
  useChatData,
  useChatInteract,
  useConfig
} from '@chainlit/react-client';
import type { IMode, IModeOption } from '@chainlit/react-client';
import { modesState } from '@chainlit/react-client';

import MemoryMeter from '@/components/chat/Memory/MemoryMeter';
import { Settings } from '@/components/icons/Settings';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'components/i18n/Translator';

import { useQuery } from '@/hooks/query';
import { useIsMobile } from '@/hooks/use-mobile';

import { chatSettingsOpenState } from '@/state/project';
import {
  IAttachment,
  attachmentsState,
  persistentCommandState
} from 'state/chat';

import { Attachments } from './Attachments';
import CommandButtons from './CommandButtons';
import CommandButton from './CommandPopoverButton';
import FavoriteButton from './FavoriteButton';
import Input, { InputMethods } from './Input';
import McpButton from './Mcp';
import ModePicker from './ModePicker';
import SkillsButton from './SkillsButton';
import SubmitButton from './SubmitButton';
import VoiceButton from './VoiceButton';

/** Raised by a follow-up chip; handled by the composer so its guards apply. */
export const FOLLOW_UP_EVENT = 'signals-follow-up';

export const askFollowUp = (question: string) =>
  window.dispatchEvent(new CustomEvent(FOLLOW_UP_EVENT, { detail: question }));

/** Raised by a starter chip. Unlike FOLLOW_UP_EVENT this only TYPES the question
 *  into the composer -- it does not send it. A starter is a suggestion, and a
 *  suggestion the user cannot read or edit before it runs is just a button that
 *  does something unexpected. */
export const PREFILL_EVENT = 'signals-prefill';

export const prefillComposer = (text: string) =>
  window.dispatchEvent(new CustomEvent(PREFILL_EVENT, { detail: text }));

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
}

// How long one socket must stay connected and idle before a handed-over
// question is sent on it.

// `fileSpec` and `onFileUploadError` stay in Props — the parent still supplies
// them and still wires drag-and-drop — but nothing in here reads them now that
// the attachment button is gone. Pasting an image still uploads, through
// onFileUpload below.
export default function MessageComposer({
  onFileUpload,
  autoScrollRef
}: Props) {
  const inputRef = useRef<InputMethods>(null);
  const [value, setValue] = useState('');
  const [selectedCommand, setSelectedCommand] = useRecoilState(
    persistentCommandState
  );
  const commands = useRecoilValue(commandsState);
  const setChatSettingsOpen = useSetRecoilState(chatSettingsOpenState);

  // The "Ask Signals why" handoff used to arrive here as ?q=<an Arabic
  // sentence the Anomalies page composed>, parked in sessionStorage and
  // replayed once the socket settled. All of that is gone: the page now sends
  // an alert ID on the socket handshake and the server asks the question, so
  // there is no sentence in a URL to park, re-ask, or get wrong.

  // Pre-select the command marked as selected by the backend
  useEffect(() => {
    const defaultSelected = commands.find((c) => c.selected);
    if (defaultSelected && !selectedCommand) {
      setSelectedCommand(defaultSelected);
    }
  }, [commands]);
  const [attachments, setAttachments] = useRecoilState(attachmentsState);
  const { t } = useTranslation();

  const { user } = useAuth();
  const { sendMessage, replyMessage } = useChatInteract();
  const {
    askUser,
    chatSettingsInputs,
    disabled: _disabled,
    connected
  } = useChatData();
  const session = useRecoilValue(sessionState);
  const socketId = session?.socket?.id;

  const disabled = _disabled || !!attachments.find((a) => !a.uploaded);

  // The question the backend offers on an answer is no longer spent as the
  // composer placeholder: it was visible only to a user who had not started
  // typing, and gone the moment they did. It is rendered as a chip under the
  // answer it belongs to (FollowUpChips), which sends it back through here.

  const { config } = useConfig();
  const showSettingsInComposer =
    config?.ui?.chat_settings_location !== 'sidebar' &&
    chatSettingsInputs.length > 0;

  const isMobile = useIsMobile();

  // Get/set available modes from state - selections are tracked via the 'default' flag on options
  const [modes, setModes] = useRecoilState(modesState);

  const handleModeSelect = useCallback(
    (modeId: string, optionId: string) => {
      setModes((prevModes) =>
        prevModes.map((mode) => {
          if (mode.id !== modeId) return mode;
          return {
            ...mode,
            options: mode.options.map((opt: IModeOption) => ({
              ...opt,
              default: opt.id === optionId
            }))
          };
        })
      );
    },
    [setModes]
  );

  // Helper to get selected option for a mode (the one with default=true, or first option)
  const getSelectedOptionId = useCallback((mode: IMode): string | undefined => {
    const defaultOpt = mode.options.find((opt) => opt.default);
    return defaultOpt?.id || mode.options[0]?.id;
  }, []);

  let promptValue = '';
  try {
    const query = useQuery();
    promptValue = query.get('prompt') || '';
  } catch {
    console.warn('Could not parse query parameters');
  }

  const [promptUsed, setPromptUsed] = useState(false);

  const onFavouriteClick = useCallback((content: string) => {
    setValue(content);
    if (inputRef.current) {
      inputRef.current.setValueExtern(content);
    }
  }, []);

  const onPaste = useCallback(
    (event: ClipboardEvent) => {
      if (event.clipboardData && event.clipboardData.items) {
        const items = Array.from(event.clipboardData.items);

        // If no text data, check for files (e.g., images)
        items.forEach((item) => {
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
              onFileUpload([file]);
            }
          }
        });
      }
    },
    [onFileUpload]
  );

  const onSubmit = useCallback(
    async (
      msg: string,
      attachments?: IAttachment[],
      selectedCommand?: string
    ) => {
      // Build modes dict: only include modes that have selections
      const modesDict: Record<string, string> = {};
      modes.forEach((mode) => {
        const selectedId = getSelectedOptionId(mode);
        if (selectedId) {
          modesDict[mode.id] = selectedId;
        }
      });

      const message: IStep = {
        threadId: '',
        command: selectedCommand,
        modes: Object.keys(modesDict).length > 0 ? modesDict : undefined,
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      const fileReferences = attachments
        ?.filter((a) => !!a.serverId)
        .map((a) => ({ id: a.serverId! }));

      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message, fileReferences);
    },
    [user, sendMessage, autoScrollRef, modes, getSelectedOptionId]
  );

  const onReply = useCallback(
    async (msg: string) => {
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      replyMessage(message);
      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
    },
    [user, replyMessage, autoScrollRef]
  );

  const submit = useCallback(() => {
    // While a choice is pending the composer is disabled, so that no new
    // question starts in the middle of an unfinished answer. But the textarea
    // stays typable, and the old guard then dropped whatever was typed without
    // sending it and without saying anything — the user's message simply
    // vanished. Treat it as their answer instead: "none of these, here is what
    // I meant" is a legitimate reply to a question.
    const answeringPendingAsk = Boolean(askUser) && value.trim() !== '';
    if (
      !answeringPendingAsk &&
      (disabled ||
        (value.trim() === '' && attachments.length === 0 && !selectedCommand))
    ) {
      return;
    }

    if (askUser) {
      onReply(value);
    } else {
      onSubmit(value, attachments, selectedCommand?.id);
    }

    setAttachments([]);
    setValue(''); // Clear the value state
    inputRef.current?.reset();
  }, [
    value,
    disabled,
    askUser,
    attachments,
    selectedCommand,
    setAttachments,
    onSubmit,
    onReply
  ]);

  /**
   * Send a specific question, as if it had been typed here.
   *
   * Goes through the composer rather than the message that raised it so that
   * every guard still applies — a pending ask is answered rather than talked
   * over, and nothing is sent while the session is disabled.
   */
  const submitText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (disabled && !askUser) return;

      if (askUser) {
        onReply(trimmed);
      } else {
        onSubmit(trimmed, attachments, selectedCommand?.id);
      }

      setAttachments([]);
      setValue('');
      inputRef.current?.reset();
    },
    [
      disabled,
      askUser,
      attachments,
      selectedCommand,
      setAttachments,
      onSubmit,
      onReply
    ]
  );

  /**
   * Run a skill: an ordinary user message - its text is what the thread
   * shows - with the form in metadata. The chat answers it without the model
   * (signals-copilot server/skill_requests.py). Same guards as a question:
   * nothing is sent while the session is disabled or a choice is pending.
   */
  const submitSkill = useCallback(
    (text: string, skill: Skill, values: SkillValues) => {
      if (disabled || askUser) return;
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: text,
        createdAt: new Date().toISOString(),
        metadata: {
          location: window.location.href,
          skill: { id: skill.id, params: skillParams(skill, values) }
        }
      };
      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message, []);
    },
    [disabled, askUser, user, sendMessage, autoScrollRef]
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string') submitText(detail);
    };
    window.addEventListener(FOLLOW_UP_EVENT, handler);
    return () => window.removeEventListener(FOLLOW_UP_EVENT, handler);
  }, [submitText]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string' && inputRef.current) {
        inputRef.current.setValueExtern(detail);
      }
    };
    window.addEventListener(PREFILL_EVENT, handler);
    return () => window.removeEventListener(PREFILL_EVENT, handler);
  }, []);

  useEffect(() => {
    if (inputRef.current && promptValue && !promptUsed) {
      const prompt = promptValue;
      if (prompt) {
        if (prompt.length > 1000) {
          inputRef.current?.setValueExtern(prompt.slice(0, 1000));
        } else {
          inputRef.current?.setValueExtern(prompt);
        }
        setPromptUsed(true);
      }
    }
  }, [promptValue, promptUsed]);

  return (
    // White with a border rather than a grey fill, and without the 96px
    // minimum height: the box no longer looks like it is waiting for a
    // paragraph when most questions are one line.
    <div
      id="message-composer"
      className="sig-composer dark:bg-card w-full flex flex-col gap-[9px] px-[14px] py-[11px]"
      style={{
        background: 'var(--sig-surface)',
        border: '1px solid var(--sig-border-strong)',
        borderRadius: 16,
        boxShadow: 'var(--sig-shadow-composer)'
      }}
    >
      {attachments.length > 0 ? (
        <div className="mb-1">
          <Attachments />
        </div>
      ) : null}
      <Input
        ref={inputRef}
        id="chat-input"
        autoFocus={!isMobile}
        selectedCommand={selectedCommand}
        setSelectedCommand={setSelectedCommand}
        onChange={setValue}
        onPaste={onPaste}
        onEnter={submit}
        placeholder={t('chat.input.placeholder')}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center -ml-1.5">
          <VoiceButton disabled={disabled} />
          {/* No attachment button. Signals answers from the tenant's own
              warehouse; a file the user uploads here is not part of it, so the
              paperclip offered something the chat cannot actually use.
              The upload plumbing (fileSpec, onFileUpload, drag-and-drop in
              chat/index.tsx) is left intact — only the affordance is gone. */}
          {showSettingsInComposer && (
            <Button
              id="chat-settings-open-modal"
              disabled={disabled}
              onClick={() => setChatSettingsOpen(true)}
              className="hover:bg-muted rounded-full"
              variant="ghost"
              size="icon"
            >
              <Settings className="!size-6" />
            </Button>
          )}
          <McpButton disabled={disabled} />
          <SkillsButton
            disabled={disabled || Boolean(askUser)}
            onRun={submitSkill}
          />
          {modes.map((mode) => (
            <ModePicker
              key={mode.id}
              mode={mode}
              disabled={disabled}
              selectedOptionId={getSelectedOptionId(mode)}
              onOptionSelect={handleModeSelect}
            />
          ))}
          <CommandButton
            disabled={disabled}
            selectedCommandId={selectedCommand?.id}
            onCommandSelect={setSelectedCommand}
          />
          <CommandButtons
            disabled={disabled}
            selectedCommandId={selectedCommand?.id}
            onCommandSelect={setSelectedCommand}
          />

          <FavoriteButton disabled={disabled} onSelect={onFavouriteClick} />
        </div>
        <div className="flex items-center gap-2">
          <MemoryMeter />
          <SubmitButton
            onSubmit={submit}
            disabled={
              disabled ||
              (!value.trim() && !selectedCommand && attachments.length === 0)
            }
          />
        </div>
      </div>
    </div>
  );
}
